import {
  isNativeEnvelope,
  isNativeNotification,
  isNativeResponse,
} from '../types/native-host';

import type {
  DeviceInfoPayload,
  NativeEnvelope,
  NativeEvent,
  NativeMethod,
  NativeMethods,
  NativeNotificationEnvelope,
  NativeNotifications,
  NativePayload,
  NativeRequestEnvelope,
  NativeResponseEnvelope,
  NativeResult,
} from '../types/native-host';

// ============================================================
// Web-side Native client
// ============================================================
//
// Promise-based RPC layer over WebView <-> React Native messaging.
// Mirrors the design from the Velog post that took bridge success
// rate from 85% to 99.5%:
//   - requestId-based request/response matching
//   - message queueing before the bridge is ready
//   - retries with exponential backoff
//   - per-request timeout
//
// Usage:
//   const info = await native.invoke('getDeviceInfo');
//   await native.invoke('openExternalUrl', { url: 'https://...' });
//   native.notify('appStateChanged', { state: 'active' });
//   const off = native.on('keyboardStateChanged', (p) => { ... });
//
// ============================================================

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (data: string) => void;
    };
    __MUZLY_IS_NATIVE_APP__?: boolean;
    __MUZLY_DEVICE_INFO__?: DeviceInfoPayload;
  }
}

// ------------------------------------------------------------
// `window` access shim
// ------------------------------------------------------------
// This module is part of `@muzly/shared` which is also resolved by
// the React Native app via tsconfig path aliases. RN's tsconfig does
// not include `lib.dom`, so referencing the bare `window` identifier
// would fail to type-check on the native side. We funnel every access
// through `getBrowserWindow()`, which goes through `globalThis` and
// works under both DOM-aware (web) and DOM-less (RN) tsconfigs.
// At runtime, the native bundle never reaches these branches because
// `getBrowserWindow()` returns undefined there.

type InboundEvent = { detail?: unknown; data?: unknown };

interface BrowserWindowLike {
  ReactNativeWebView?: {
    postMessage: (data: string) => void;
  };
  __MUZLY_IS_NATIVE_APP__?: boolean;
  __MUZLY_DEVICE_INFO__?: DeviceInfoPayload;
  addEventListener(type: string, handler: (event: InboundEvent) => void): void;
}

const getBrowserWindow = (): BrowserWindowLike | undefined => {
  const g = globalThis as { window?: BrowserWindowLike };
  const win = g.window;
  // RN/Hermes exposes a partial `window` global that lacks DOM APIs, so a
  // bare `typeof window` check is truthy on native and the client would call
  // `win.addEventListener` (undefined) → crash on boot. Only treat a real DOM
  // window (with addEventListener) as the browser window; the native bundle
  // then stays inert here, as intended.
  return win && typeof win.addEventListener === 'function' ? win : undefined;
};

export const isRunningInNativeApp = (): boolean => {
  const win = getBrowserWindow();
  return !!win && !!win.__MUZLY_IS_NATIVE_APP__;
};

export interface NativeInvokeOptions {
  /** Timeout for a single attempt, in ms. Default 5000. */
  timeout?: number;
  /** Total attempt count (initial + retries). Default 3. */
  retries?: number;
}

const DEFAULT_TIMEOUT = 5_000;
const DEFAULT_RETRIES = 3;
const READY_POLL_INTERVAL = 100;
const READY_POLL_MAX_MS = 5_000;
const BACKOFF_BASE_MS = 500;

type NotificationHandler<E extends NativeEvent> = (
  payload: NativePayload<E>,
) => void;

interface PendingRequest {
  method: NativeMethod;
  options: Required<NativeInvokeOptions>;
  resolve: (value: unknown) => void;
  reject: (err: Error) => void;
  attempt: number;
  timer: ReturnType<typeof setTimeout> | null;
}

type QueuedItem =
  | {
      kind: 'request';
      envelope: NativeRequestEnvelope;
      pending: PendingRequest;
    }
  | {
      kind: 'notification';
      envelope: NativeNotificationEnvelope;
    };

const generateRequestId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Conditional argument tuple: when params type is `void`, the second
 * argument is optional; otherwise it is required. Lets callers write
 * `invoke('getDeviceInfo')` without passing a params arg.
 */
export type NativeInvokeArgs<M extends NativeMethod> =
  NativeMethods[M]['params'] extends void
    ? [method: M, params?: void, options?: NativeInvokeOptions]
    : [
        method: M,
        params: NativeMethods[M]['params'],
        options?: NativeInvokeOptions,
      ];

export type NativeNotifyArgs<E extends NativeEvent> =
  NativeNotifications[E] extends void
    ? [event: E, payload?: void]
    : [event: E, payload: NativeNotifications[E]];

export class Native {
  private isReady = false;
  private readyPollElapsed = 0;
  private outboundQueue: QueuedItem[] = [];
  private pending = new Map<string, PendingRequest>();
  private listeners = new Map<
    NativeEvent,
    Set<NotificationHandler<NativeEvent>>
  >();
  // Buffer notifications that arrive before any listener subscribes
  // for that event. Drained as soon as the first listener for the
  // event registers via on(). Handles the cold-start race where the
  // native shell pushes a deep link before the web bundle's React
  // tree has mounted.
  private notificationBuffer = new Map<NativeEvent, unknown[]>();

  constructor() {
    if (!getBrowserWindow()) return;
    this.subscribeInbound();
    this.checkReady();
  }

  // ---------------------------------------------------------
  // Public: RPC
  // ---------------------------------------------------------

  /**
   * Invoke a native RPC method and await the response.
   *
   *   const info = await native.invoke('getDeviceInfo');
   *   await native.invoke('openExternalUrl', { url: 'https://...' });
   */
  invoke<M extends NativeMethod>(
    ...args: NativeInvokeArgs<M>
  ): Promise<NativeResult<M>> {
    const method = args[0];
    const params = args[1] as NativeMethods[M]['params'];
    const options = args[2];

    if (!getBrowserWindow()) {
      return Promise.reject(
        new Error('native.invoke: no window (SSR context)'),
      );
    }

    const opts: Required<NativeInvokeOptions> = {
      timeout: options?.timeout ?? DEFAULT_TIMEOUT,
      retries: options?.retries ?? DEFAULT_RETRIES,
    };

    return new Promise<NativeResult<M>>((resolve, reject) => {
      const envelope: NativeRequestEnvelope = {
        kind: 'request',
        id: generateRequestId(),
        method,
        params,
      };

      const pending: PendingRequest = {
        method,
        options: opts,
        resolve: resolve as (value: unknown) => void,
        reject,
        attempt: 1,
        timer: null,
      };

      if (this.isReady) {
        this.dispatchRequest(envelope, pending);
      } else {
        this.outboundQueue.push({ kind: 'request', envelope, pending });
      }
    });
  }

  // ---------------------------------------------------------
  // Public: Notifications
  // ---------------------------------------------------------

  /**
   * Fire-and-forget event toward native. Use for state updates that
   * don't need a response (app lifecycle hints, keyboard state, etc.).
   */
  notify<E extends NativeEvent>(...args: NativeNotifyArgs<E>): void {
    const event = args[0];
    const payload = args[1];
    const envelope: NativeNotificationEnvelope = {
      kind: 'notification',
      event,
      payload,
    };

    if (this.isReady) {
      this.postEnvelope(envelope);
    } else {
      this.outboundQueue.push({ kind: 'notification', envelope });
    }
  }

  /**
   * Subscribe to notifications coming FROM native. Returns an
   * unsubscribe function.
   *
   * If notifications for this event arrived before any listener
   * subscribed (e.g. a deep link delivered during cold start while
   * the React tree was still mounting), they're delivered to this
   * handler immediately on subscribe and the buffer is cleared.
   */
  on<E extends NativeEvent>(
    event: E,
    handler: NotificationHandler<E>,
  ): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler as NotificationHandler<NativeEvent>);

    // Flush any buffered notifications for this event.
    const buffered = this.notificationBuffer.get(event);
    if (buffered && buffered.length > 0) {
      this.notificationBuffer.delete(event);
      buffered.forEach((payload) => {
        try {
          handler(payload as NativePayload<E>);
        } catch (err) {
          console.warn(
            `[native] buffered notification handler failed (event=${event}):`,
            err,
          );
        }
      });
    }

    return () => {
      set?.delete(handler as NotificationHandler<NativeEvent>);
    };
  }

  // ---------------------------------------------------------
  // Internal: outbound
  // ---------------------------------------------------------

  private dispatchRequest(
    envelope: NativeRequestEnvelope,
    pending: PendingRequest,
  ): void {
    this.pending.set(envelope.id, pending);

    pending.timer = setTimeout(() => {
      this.handleAttemptFailure(
        envelope,
        pending,
        new Error(`native.invoke timeout: ${envelope.method}`),
      );
    }, pending.options.timeout);

    this.postEnvelope(envelope);
  }

  private handleAttemptFailure(
    envelope: NativeRequestEnvelope,
    pending: PendingRequest,
    error: Error,
  ): void {
    this.pending.delete(envelope.id);
    if (pending.timer) {
      clearTimeout(pending.timer);
      pending.timer = null;
    }

    if (pending.attempt >= pending.options.retries) {
      pending.reject(error);
      return;
    }

    const nextAttempt = pending.attempt + 1;
    const delay = BACKOFF_BASE_MS * Math.pow(2, pending.attempt - 1);

    setTimeout(() => {
      const retryEnvelope: NativeRequestEnvelope = {
        ...envelope,
        id: generateRequestId(),
      };
      const retryPending: PendingRequest = {
        ...pending,
        attempt: nextAttempt,
        timer: null,
      };
      this.dispatchRequest(retryEnvelope, retryPending);
    }, delay);
  }

  private flushQueue(): void {
    while (this.outboundQueue.length > 0) {
      const item = this.outboundQueue.shift();
      if (!item) continue;
      if (item.kind === 'request') {
        this.dispatchRequest(item.envelope, item.pending);
      } else {
        this.postEnvelope(item.envelope);
      }
    }
  }

  private postEnvelope(envelope: NativeEnvelope): void {
    const win = getBrowserWindow();
    if (!win) return;
    const channel = win.ReactNativeWebView;
    if (!channel) {
      return;
    }
    try {
      channel.postMessage(JSON.stringify(envelope));
    } catch (err) {
      console.warn('[native] postMessage failed:', err);
    }
  }

  // ---------------------------------------------------------
  // Internal: inbound
  // ---------------------------------------------------------

  private subscribeInbound(): void {
    const win = getBrowserWindow();
    if (!win) return;

    const handleCustom = (event: InboundEvent) => {
      this.routeIncoming(event.detail);
    };
    const handleMessage = (event: InboundEvent) => {
      try {
        const data = event.data;
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        this.routeIncoming(parsed);
      } catch {
        // Not a JSON message we care about
      }
    };

    win.addEventListener('muzly-native-message', handleCustom);
    win.addEventListener('message', handleMessage);
  }

  private routeIncoming(data: unknown): void {
    if (!isNativeEnvelope(data)) return;

    if (isNativeResponse(data)) {
      this.handleResponse(data);
    } else if (isNativeNotification(data)) {
      this.handleNotification(data);
    }
  }

  private handleResponse(envelope: NativeResponseEnvelope): void {
    const pending = this.pending.get(envelope.id);
    if (!pending) return;

    this.pending.delete(envelope.id);
    if (pending.timer) {
      clearTimeout(pending.timer);
      pending.timer = null;
    }

    if (envelope.ok) {
      pending.resolve(envelope.result);
    } else {
      pending.reject(new Error(envelope.error));
    }
  }

  private handleNotification(envelope: NativeNotificationEnvelope): void {
    const handlers = this.listeners.get(envelope.event);
    if (!handlers || handlers.size === 0) {
      // No subscriber yet — buffer the payload so the next on()
      // for this event can replay it. Critical for cold-start
      // notifications (e.g. deepLink) that arrive before React
      // mounts.
      const buf = this.notificationBuffer.get(envelope.event) ?? [];
      buf.push(envelope.payload);
      this.notificationBuffer.set(envelope.event, buf);
      return;
    }
    handlers.forEach((handler) => {
      try {
        handler(envelope.payload as NativePayload<NativeEvent>);
      } catch (err) {
        console.warn(
          `[native] notification handler failed (event=${envelope.event}):`,
          err,
        );
      }
    });
  }

  // ---------------------------------------------------------
  // Internal: readiness
  // ---------------------------------------------------------

  private checkReady(): void {
    const win = getBrowserWindow();
    if (!win) return;

    const ready = !!win.ReactNativeWebView || !!win.__MUZLY_IS_NATIVE_APP__;

    if (ready) {
      this.isReady = true;
      this.flushQueue();
      return;
    }

    if (this.readyPollElapsed >= READY_POLL_MAX_MS) {
      this.isReady = true;
      this.flushQueue();
      return;
    }

    this.readyPollElapsed += READY_POLL_INTERVAL;
    setTimeout(() => this.checkReady(), READY_POLL_INTERVAL);
  }
}

/**
 * Singleton Native client for the web app.
 *
 *   import { native } from '@muzly/shared';
 *   const info = await native.invoke('getDeviceInfo');
 */
export const native = new Native();
